import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import imageCompression from 'browser-image-compression';
import Modal from './Modal';
import { RestaurantFoodResponse, RestaurantCategoryWithFoodsResponse } from '../../types/restaurants';
import { uploadService } from '../../services/uploadService';
import { imageIdToUrl } from '../../utils/imageUtils';

interface EditFoodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    foodId: number;
    categoryId?: number;
    name?: string;
    description?: string;
    price?: number;
    isAvailable?: boolean;
    foodImageIds?: string[];
  }) => void;
  food: RestaurantFoodResponse & { categoryId: number };
  categories: RestaurantCategoryWithFoodsResponse[];
  loading?: boolean;
}

const EditFoodModal: React.FC<EditFoodModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  food,
  categories,
  loading = false
}) => {
  const { t } = useTranslation();
  const [categoryId, setCategoryId] = useState<number>(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [isAvailable, setIsAvailable] = useState(false);
  const [foodImages, setFoodImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && food) {
      setCategoryId(food.categoryId);
      setName(food.name);
      setDescription(food.description);
      setPrice(food.price.toString());
      setIsAvailable(food.isAvailable);
      setFoodImages([]);
      setImagePreviews([]);
      setUploadError('');
    }
  }, [isOpen, food]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) return;

    // Validate files
    const validFiles: File[] = [];
    let hasError = false;

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setUploadError(t('modals.editFood.invalidFileType'));
        hasError = true;
        break;
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setUploadError(t('modals.editFood.fileTooLarge'));
        hasError = true;
        break;
      }

      validFiles.push(file);
    }

    if (hasError) return;

    setFoodImages(validFiles);
    setUploadError('');

    // Create previews from original files
    const previews: string[] = [];
    let loadedCount = 0;

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        previews.push(reader.result as string);
        loadedCount++;

        if (loadedCount === validFiles.length) {
          setImagePreviews(previews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImages = () => {
    setFoodImages([]);
    setImagePreviews([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updates: any = {
      foodId: food.id
    };

    if (categoryId !== food.categoryId && categoryId > 0) {
      updates.categoryId = categoryId;
    }
    if (name !== food.name && name.trim()) {
      updates.name = name.trim();
    }
    if (description !== food.description) {
      updates.description = description.trim();
    }
    const priceValue = parseInt(price);
    if (!isNaN(priceValue) && priceValue !== food.price) {
      updates.price = priceValue;
    }
    if (isAvailable !== food.isAvailable) {
      updates.isAvailable = isAvailable;
    }

    // Upload food images if new ones are selected
    if (foodImages.length > 0) {
      setUploading(true);
      setUploadError('');

      try {
        // Compression options
        const options = {
          maxSizeMB: 1, // Maximum file size in MB
          maxWidthOrHeight: 1920, // Maximum width or height
          useWebWorker: true, // Use web worker for better performance
          fileType: 'image/jpeg' as const, // Convert to JPEG for better compression
        };

        const uploadedIds: string[] = [];

        for (const file of foodImages) {
          // Compress each image before uploading
          const compressedFile = await imageCompression(file, options);
          const response = await uploadService.uploadImage(compressedFile);

          if (response.success && response.data) {
            uploadedIds.push(response.data.id);
          } else {
            setUploadError(response.error || t('modals.editFood.uploadFailed'));
            setUploading(false);
            return;
          }
        }

        updates.foodImageIds = uploadedIds;
      } catch (error) {
        setUploadError(t('modals.editFood.uploadFailed'));
        setUploading(false);
        return;
      } finally {
        setUploading(false);
      }
    }

    onSubmit(updates);
  };

  const handleClose = () => {
    setCategoryId(food.categoryId);
    setName(food.name);
    setDescription(food.description);
    setPrice(food.price.toString());
    setIsAvailable(food.isAvailable);
    setFoodImages([]);
    setImagePreviews([]);
    setUploadError('');
    onClose();
  };

  const hasChanges = () => {
    const priceValue = parseInt(price);
    return (
      categoryId !== food.categoryId ||
      name !== food.name ||
      description !== food.description ||
      (!isNaN(priceValue) && priceValue !== food.price) ||
      isAvailable !== food.isAvailable ||
      foodImages.length > 0
    );
  };

  const currentImageUrl = food.foodImage ? imageIdToUrl(food.foodImage) : null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('modals.editFood.title')}>
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="modal-form-group">
          <label htmlFor="category">{t('modals.editFood.category')}</label>
          <select
            id="category"
            value={categoryId}
            onChange={(e) => setCategoryId(parseInt(e.target.value))}
            disabled={loading || uploading}
          >
            <option value={0}>{t('modals.editFood.selectCategory')}</option>
            {categories.map((category, index) => (
              <option key={index} value={category.categoryId}>
                {category.categoryName}
              </option>
            ))}
          </select>
        </div>

        <div className="modal-form-group">
          <label htmlFor="name">{t('modals.editFood.name')}</label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading || uploading}
          />
        </div>

        <div className="modal-form-group">
          <label htmlFor="description">{t('modals.editFood.description')}</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading || uploading}
            rows={4}
          />
        </div>

        <div className="modal-form-group">
          <label htmlFor="price">{t('modals.editFood.price')}</label>
          <input
            id="price"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            min="0"
            disabled={loading || uploading}
          />
          <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem' }}>
            {t('modals.editFood.priceHint')}
          </small>
        </div>

        <div className="modal-form-group">
          <label htmlFor="isAvailable">{t('modals.editFood.availability')}</label>
          <select
            id="isAvailable"
            value={isAvailable.toString()}
            onChange={(e) => setIsAvailable(e.target.value === 'true')}
            disabled={loading || uploading}
          >
            <option value="true">{t('modals.editFood.available')}</option>
            <option value="false">{t('modals.editFood.unavailable')}</option>
          </select>
        </div>

        <div className="modal-form-group">
          <label>{t('modals.editFood.images')}</label>

          {/* Current image */}
          {currentImageUrl && imagePreviews.length === 0 && (
            <div style={{
              marginBottom: '1rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              overflow: 'hidden',
              maxWidth: '200px'
            }}>
              <img
                src={currentImageUrl}
                alt="Current Food"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '150px',
                  objectFit: 'cover',
                  display: 'block'
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Preview new images */}
          {imagePreviews.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              marginBottom: '1rem',
              flexWrap: 'wrap'
            }}>
              {imagePreviews.map((preview, index) => (
                <div key={index} style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  width: '100px',
                  height: '100px'
                }}>
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* File input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            disabled={loading || uploading}
            style={{ display: 'block', marginBottom: '0.5rem' }}
          />

          {foodImages.length > 0 && (
            <button
              type="button"
              onClick={handleRemoveImages}
              className="modal-button secondary"
              disabled={loading || uploading}
              style={{ marginTop: '0.5rem', padding: '0.3rem 0.6rem', fontSize: '0.85rem' }}
            >
              {t('modals.editFood.removeImages')}
            </button>
          )}

          <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
            {t('modals.editFood.imageHint')}
          </small>

          {uploadError && (
            <div style={{
              color: '#dc3545',
              fontSize: '0.85rem',
              marginTop: '0.5rem',
              padding: '0.5rem',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px'
            }}>
              {uploadError}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-button secondary"
            disabled={loading || uploading}
          >
            {t('modals.editFood.cancel')}
          </button>
          <button
            type="submit"
            className="modal-button primary"
            disabled={loading || uploading || !hasChanges()}
          >
            {uploading
              ? t('modals.editFood.uploading')
              : loading
                ? t('modals.editFood.saving')
                : t('modals.editFood.save')
            }
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditFoodModal;
